import { useEffect, useMemo, useState } from 'react'

import ReactPaginate from 'react-paginate'
import { Col, Form, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap'
import { toast } from 'react-toastify'

import {
  ArrowClockwise,
  CaretDown,
  CaretUp,
  PencilFill,
  PlusCircleFill,
  Search,
  TrashFill,
} from 'react-bootstrap-icons'

import Navbar from '../../../components/navbar/navbar'
import { CustomSpinner } from '../../../components/CustomSpinner'
import { IconButton } from '../../../components/IconButton'
import { CustomButton } from '../../../components/CustomButton'

import { sendErrorApiMessage } from '../../../utils/sendErrorApiMessage'

import { useOrderTable } from '../../../hooks/useOrderTable'

import { Ask } from '../../../components/Ask'
import EmbalagensService from '../../../services/database/EmbalagensService'
import { CustomIntegerInput } from '../../../components/CustomDecimalInput'
import { useCommand } from '../../../hooks/useCommand'
import { ModalEmb } from './ModalEmb'

export function Embalagens() {
  const [embalagens, setEmbalagens] = useState([])
  const [isMotivosLoading, setIsEmbalagensLoading] = useState(true)

  const [selectedId, setSelectedId] = useState('')
  const [search, setSearch] = useState({
    type: 'descricao',
    text: '',
  })

  const [isCreateEmbalagemLoading, setIsCreateEmbalagemLoading] =
    useState(false)
  const [isUpdateEmbalagemLoading, setIsUpdateEmbalagemLoading] =
    useState(false)
  const [isSearchEmbalagemLoading, setIsSearchEmbalagemLoading] =
    useState(false)
  const [isDeleteEmbalagemLoading, setIsDeleteEmbalagemLoading] = useState({})

  const [pageCount, setPageCount] = useState(0)
  const [itemOffset, setItemOffset] = useState(0)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  const [isModalCreateEmbalagemOpen, setIsModalCreateEmbalagemOpen] =
    useState(false)
  const [isModalUpdateEmbalagemOpen, setIsModalUpdateEmbalagemOpen] =
    useState(false)

  const endOffset = itemOffset + itemsPerPage

  const {
    sortedArray: sortedEmbalagens,
    check,
    handleOrganize,
    orderType,
  } = useOrderTable(embalagens, 'idembalagem', 'asc')

  const filteredMotivos = useMemo(() => {
    setPageCount(Math.ceil(sortedEmbalagens.length / itemsPerPage))

    return sortedEmbalagens.slice(itemOffset, endOffset)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endOffset, itemOffset, sortedEmbalagens, orderType.field, orderType.type])

  const handlePageClick = (event) => {
    const newOffset = (event.selected * itemsPerPage) % embalagens.length

    setItemOffset(newOffset)
  }

  async function handleCreateEmbalagem({ descricao, embalagem }) {
    try {
      setIsCreateEmbalagemLoading(true)

      await EmbalagensService.create({ embalagem, descricao })

      toast.success('Embalagem criada com sucesso!')
      loadEmbalagens()
      setIsModalCreateEmbalagemOpen(false)
    } catch (err) {
      sendErrorApiMessage(err, 'Não foi possível criar a embalagem!')
    } finally {
      setIsCreateEmbalagemLoading(false)
    }
  }

  async function handleSearchEmbalagem() {
    try {
      setIsSearchEmbalagemLoading(true)

      const data = await EmbalagensService.search(search)

      setEmbalagens(data)
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao buscar embalagem!')
    } finally {
      setIsSearchEmbalagemLoading(false)
    }
  }

  async function handleUpdateEmbalagem(data) {
    const { id, descricao, embalagem } = data

    try {
      setIsUpdateEmbalagemLoading(true)
      await EmbalagensService.update(id, { descricao, embalagem })

      loadEmbalagens()
      toast.success(`Embalagem atualizada com sucesso!`)
      setIsModalUpdateEmbalagemOpen(false)
    } catch (err) {
      sendErrorApiMessage(err, 'Não foi possível atualizar o embalagem!')
    } finally {
      setIsUpdateEmbalagemLoading(false)
    }
  }

  async function handleDeleteEmbalagem(id, index) {
    try {
      setIsDeleteEmbalagemLoading((prevState) => ({
        ...prevState,
        [index]: true,
      }))

      await EmbalagensService.delete(id)

      loadEmbalagens()
      toast.success('Embalagem deletada com sucesso!')
    } catch (err) {
      sendErrorApiMessage(err, 'Não foi possível deletar a embalagem!')
    } finally {
      setIsDeleteEmbalagemLoading((prevState) => ({
        ...prevState,
        [index]: false,
      }))
    }
  }

  async function loadEmbalagens() {
    try {
      setIsEmbalagensLoading(true)

      const data = await EmbalagensService.getAll()

      setEmbalagens(data)
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao carregar as embalagens!')
    } finally {
      setIsEmbalagensLoading(false)
    }
  }

  useCommand(() => {
    setSearch((prevState) => ({
      ...prevState,
      text: '',
    }))

    loadEmbalagens()
  })

  useEffect(() => {
    loadEmbalagens()
  }, [])

  return (
    <>
      <Navbar />

      <div className="mt-page">
        <h1>Embalagens</h1>

        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 10,
          }}
        >
          <section>
            <p>Embalagens por página</p>
            <Form.Select
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value))
              }}
              style={{
                width: '200px',
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Form.Select>
          </section>

          <CustomButton
            title="Cadastrar"
            color="#27ae60"
            onClick={() => setIsModalCreateEmbalagemOpen(true)}
          >
            <PlusCircleFill />
          </CustomButton>

          <CustomButton
            title="Recarregar"
            color="#0070da"
            onClick={loadEmbalagens}
            isLoading={isMotivosLoading}
            isLoadingText="Recarregando..."
          >
            <ArrowClockwise />
          </CustomButton>
        </div>

        <Row className="mt-3">
          <Col>
            <Form.Select
              value={search.type}
              onChange={(e) =>
                setSearch((prevState) => ({
                  ...prevState,
                  type: e.target.value,
                }))
              }
            >
              <option value="idembalagem">ID</option>
              <option value="descricao">Descrição</option>
              <option value="embalagem">Embalagem</option>
            </Form.Select>
          </Col>

          <Col>
            {search.type === 'id' ? (
              <CustomIntegerInput
                placeholder="Pesquise aqui..."
                value={search.text}
                onChange={(e) =>
                  setSearch((prevState) => ({
                    ...prevState,
                    text: e.target.value,
                  }))
                }
                onKeyDown={(e) => {
                  if (e.target.value.trim().length > 0 && e.key === 'Enter') {
                    handleSearchEmbalagem()
                  }
                }}
              />
            ) : (
              <Form.Control
                placeholder="Pesquise aqui..."
                value={search.text}
                onChange={(e) =>
                  setSearch((prevState) => ({
                    ...prevState,
                    text: e.target.value,
                  }))
                }
                onKeyDown={(e) => {
                  if (e.target.value.trim().length > 0 && e.key === 'Enter') {
                    handleSearchEmbalagem()
                  }
                }}
              />
            )}
          </Col>

          <Col>
            <IconButton
              isLoading={isSearchEmbalagemLoading}
              onClick={handleSearchEmbalagem}
              isDisabled={!search.text.trim()}
            >
              <Search />
            </IconButton>
          </Col>
        </Row>

        {isMotivosLoading ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 50,
            }}
          >
            <CustomSpinner />
          </div>
        ) : filteredMotivos.length === 0 ? (
          <div
            style={{
              flex: 1,
              minHeight: '100%',
              padding: 50,
              textAlign: 'center',
            }}
          >
            <p>Sem embalagens cadastradas!</p>
          </div>
        ) : (
          <>
            <Table
              striped
              bordered
              hover
              responsive
            >
              <thead>
                <tr>
                  <th onClick={() => handleOrganize('idembalagem')}>
                    <span>ID</span>
                    <CaretUp color={check('idembalagem', 'asc')} />
                    <CaretDown color={check('idembalagem', 'desc')} />
                  </th>

                  <th onClick={() => handleOrganize('descricao')}>
                    <span>Descrição</span>
                    <CaretUp color={check('descricao', 'asc')} />
                    <CaretDown color={check('descricao', 'desc')} />
                  </th>

                  <th onClick={() => handleOrganize('embalagem')}>
                    <span>Embalagem</span>
                    <CaretUp color={check('embalagem', 'asc')} />
                    <CaretDown color={check('embalagem', 'desc')} />
                  </th>

                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredMotivos.map((embalagem, index) => (
                  <tr key={embalagem.idembalagem}>
                    <td>{embalagem.idembalagem}</td>
                    <td>{embalagem.descricao}</td>
                    <td>{embalagem.embalagem}</td>
                    <td
                      style={{
                        display: 'flex',
                        gap: 5,
                      }}
                    >
                      <OverlayTrigger overlay={<Tooltip>Editar</Tooltip>}>
                        <IconButton
                          color="#e67e22"
                          onClick={() => {
                            setSelectedId(embalagem.idembalagem)
                            setIsModalUpdateEmbalagemOpen(true)
                          }}
                        >
                          <PencilFill />
                        </IconButton>
                      </OverlayTrigger>

                      <OverlayTrigger overlay={<Tooltip>Excluir</Tooltip>}>
                        <IconButton
                          isLoading={isDeleteEmbalagemLoading[index]}
                          color="#c0392b"
                          onClick={() => {
                            Ask('Tem certeza que deseja excluir?', () =>
                              handleDeleteEmbalagem(
                                embalagem.idembalagem,
                                index,
                              ),
                            )
                          }}
                        >
                          <TrashFill />
                        </IconButton>
                      </OverlayTrigger>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <ReactPaginate
              breakLabel="..."
              nextLabel="Próxima"
              onPageChange={handlePageClick}
              pageRangeDisplayed={5}
              pageCount={pageCount}
              previousLabel="Anterior"
              renderOnZeroPageCount={null}
              containerClassName="pagination"
              activeClassName="active"
            />
          </>
        )}
      </div>

      {isModalCreateEmbalagemOpen && (
        <ModalEmb
          isOpen={isModalCreateEmbalagemOpen}
          onClose={() => setIsModalCreateEmbalagemOpen(false)}
          title="Criar Embalagem"
          isRequestLoading={isCreateEmbalagemLoading}
          onRequest={handleCreateEmbalagem}
        />
      )}

      {isModalUpdateEmbalagemOpen && (
        <ModalEmb
          id={selectedId}
          isOpen={isModalUpdateEmbalagemOpen}
          onClose={() => setIsModalUpdateEmbalagemOpen(false)}
          title="Editar Embalagem"
          isRequestLoading={isUpdateEmbalagemLoading}
          onRequest={handleUpdateEmbalagem}
        />
      )}
    </>
  )
}
